import { useEffect } from 'react';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import PopupTemplate from '@arcgis/core/PopupTemplate';
import { useTennesseeMemphisDemoEnabled } from 'utils/featureFlags';
import {
  TNNG_MEMPHIS_ASSETS_LAYER_ID,
  TNNG_MEMPHIS_ASSETS_LAYER_TITLE,
  TNNG_MEMPHIS_FACILITIES,
  TNNG_MEMPHIS_SYMBOLOGY,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/tennesseeMemphisThreatNotification';
import type { ArcgisMapView } from '../utils/arcgisView';
import {
  symbolToPictureMarkerMap,
  type UscgSymbol,
} from '../utils/uscgSymbology';

const ASSET_POPUP = new PopupTemplate({
  title: '{name}',
  content: [
    {
      type: 'fields',
      fieldInfos: [
        { fieldName: 'city', label: 'City' },
        { fieldName: 'state', label: 'State' },
      ],
    },
  ],
});

function pictureMarkerFor(symbology: string) {
  return (
    symbolToPictureMarkerMap[symbology as UscgSymbol] ??
    symbolToPictureMarkerMap[TNNG_MEMPHIS_SYMBOLOGY]
  );
}

function buildTnngAssetGraphics(): Graphic[] {
  return TNNG_MEMPHIS_FACILITIES.map(
    (facility) =>
      new Graphic({
        geometry: new Point({
          longitude: facility.lon,
          latitude: facility.lat,
        }),
        symbol: pictureMarkerFor(facility.symbology),
        attributes: {
          id: facility.id,
          name: facility.name,
          city: facility.city,
          state: facility.state,
        },
        popupTemplate: ASSET_POPUP,
      })
  );
}

function ensureLayer(
  mapView: ArcgisMapView,
  id: string,
  title: string
): GraphicsLayer {
  const existing = mapView.map?.findLayerById(id) as GraphicsLayer | undefined;
  if (existing) return existing;
  const layer = new GraphicsLayer({ id, title, listMode: 'show' });
  mapView.map?.add(layer);
  return layer;
}

function removeLayerById(mapView: ArcgisMapView, id: string) {
  const layer = mapView.map?.findLayerById(id);
  if (layer) mapView.map?.remove(layer);
}

/** TNG-only Memphis facility pins with per-asset USCG picture markers. */
export function useEnsureTennesseeMemphisAssetLayer({
  mapView,
}: {
  mapView: ArcgisMapView | null;
}) {
  const tennesseeMemphisDemo = useTennesseeMemphisDemoEnabled();

  useEffect(() => {
    if (!mapView?.map) return;

    if (!tennesseeMemphisDemo) {
      removeLayerById(mapView, TNNG_MEMPHIS_ASSETS_LAYER_ID);
      return;
    }

    const layer = ensureLayer(
      mapView,
      TNNG_MEMPHIS_ASSETS_LAYER_ID,
      TNNG_MEMPHIS_ASSETS_LAYER_TITLE
    );
    layer.removeAll();
    layer.addMany(buildTnngAssetGraphics());

    return () => {
      removeLayerById(mapView, TNNG_MEMPHIS_ASSETS_LAYER_ID);
    };
  }, [mapView, tennesseeMemphisDemo]);
}
